// src/theme/colors.js

// Dark theme colors (default)
const darkColors = {
  // Main background
  bgGradientFrom: 'from-gray-700',
  bgGradientVia: 'via-teal-600/10',
  bgGradientTo: 'to-teal-800',

  // Text colors
  textPrimary: 'text-white',
  textSecondary: 'text-gray-300',
  textMuted: 'text-gray-400',
  textHighlight: 'text-teal-300',
  textFooter: 'text-teal-400',

  // Button gradients
  btnPrimaryFrom: 'from-teal-500',
  btnPrimaryTo: 'to-emerald-600',
  btnSecondaryFrom: 'from-gray-600',
  btnSecondaryTo: 'to-gray-700',

  // Card and container backgrounds
  cardBg: 'bg-gray-800',
  cardBgOpacity: 'bg-opacity-60',
  cardBorder: 'border-teal-800',
  cardBorderHover: 'border-teal-500',
  cardBgHover: 'bg-gray-700',

  // Form elements
  inputBg: 'bg-gray-700',
  inputBgDisabled: 'bg-gray-600',
  inputBorder: 'border-gray-600',
  inputFocusBorder: 'focus:border-teal-500',
  inputFocusRing: 'focus:ring-teal-500',

  // Table elements
  tableHeaderBg: 'bg-gray-800',
  tableBorderColor: 'border-gray-700',
  tableRowHover: 'hover:bg-gray-700',

  // Status colors
  positive: 'text-emerald-400',
  negative: 'text-red-400',

  // Utility functions
  getBalanceColor: amount => (amount >= 0 ? 'text-emerald-400' : 'text-red-400')
}

// Light theme colors
const lightColors = {
  // Main background
  bgGradientFrom: 'from-gray-100',
  bgGradientVia: 'via-teal-50',
  bgGradientTo: 'to-teal-100',

  // Text colors
  textPrimary: 'text-gray-900',
  textSecondary: 'text-gray-700',
  textMuted: 'text-gray-500',
  textHighlight: 'text-teal-600',
  textFooter: 'text-teal-500',

  // Button gradients
  btnPrimaryFrom: 'from-teal-500',
  btnPrimaryTo: 'to-emerald-600',
  btnSecondaryFrom: 'from-gray-200',
  btnSecondaryTo: 'to-gray-300',

  // Card and container backgrounds
  cardBg: 'bg-white',
  cardBgOpacity: 'bg-opacity-90',
  cardBorder: 'border-teal-200',
  cardBorderHover: 'border-teal-400',
  cardBgHover: 'bg-gray-50',

  // Form elements
  inputBg: 'bg-white',
  inputBgDisabled: 'bg-gray-100',
  inputBorder: 'border-gray-300',
  inputFocusBorder: 'focus:border-teal-500',
  inputFocusRing: 'focus:ring-teal-500',

  // Table elements
  tableHeaderBg: 'bg-gray-50',
  tableBorderColor: 'border-gray-200',
  tableRowHover: 'hover:bg-gray-50',

  // Status colors
  positive: 'text-emerald-600',
  negative: 'text-red-600',

  // Utility functions
  getBalanceColor: amount => (amount >= 0 ? 'text-emerald-600' : 'text-red-600')
}

// Society-specific palettes for both modes
const societyPalettes = {
  default: {
    dark: { primary: '#14B8A6', secondary: '#0EA5E9', accent: '#A78BFA' },
    light: { primary: '#0EA5E9', secondary: '#14B8A6', accent: '#7C3AED' }
  },
  // Example society IDs/names; replace keys with actual IDs
  'Al-Noor Developer': {
    dark: { primary: '#F59E0B', secondary: '#10B981', accent: '#6366F1' },
    light: { primary: '#D97706', secondary: '#059669', accent: '#4F46E5' }
  },
  'Al-Noor Garden': {
    dark: { primary: '#EF4444', secondary: '#22D3EE', accent: '#84CC16' },
    light: { primary: '#DC2626', secondary: '#06B6D4', accent: '#65A30D' }
  },
  'Gulshan-E-Habib City': {
    dark: { primary: '#8B5CF6', secondary: '#F472B6', accent: '#22C55E' },
    light: { primary: '#7C3AED', secondary: '#DB2777', accent: '#16A34A' }
  }
}

// Function to get current theme colors
const getColors = () => {
  const isDark = document.documentElement.classList.contains('dark');
  return isDark ? darkColors : lightColors;
}

// Helper: get society palette by key (id or name)
const getSocietyPalette = (societyKey) => {
  const isDark = document.documentElement.classList.contains('dark');
  const palette = societyPalettes[societyKey] || societyPalettes.default
  return isDark ? palette.dark : palette.light
}

// Export themes, palettes and getters
export { darkColors, lightColors, getColors, societyPalettes, getSocietyPalette }
export default getColors
